<script setup lang="ts">
const { t } = useI18n();

useHead({
  titleTemplate: () => t("intentPages.viberBotRegistration.title") || t("intentPages.viberBotRegistration.heroTitle"),
  meta: [
    { name: "description", content: () => t("intentPages.viberBotRegistration.subtitle") || t("intentPages.viberBotRegistration.heroSubtitle") },
    { property: "og:title", content: () => t("intentPages.viberBotRegistration.title") || t("intentPages.viberBotRegistration.heroTitle") },
    { property: "og:description", content: () => t("intentPages.viberBotRegistration.subtitle") || t("intentPages.viberBotRegistration.heroSubtitle") },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
});

const faqItems = ref([
  {
    question: t('intentPages.viberBotRegistration.faq.q1'),
    answer: t('intentPages.viberBotRegistration.faq.a1'),
    isOpen: false
  },
  {
    question: t('intentPages.viberBotRegistration.faq.q2'),
    answer: t('intentPages.viberBotRegistration.faq.a2'),
    isOpen: false
  },
  {
    question: t('intentPages.viberBotRegistration.faq.q3'),
    answer: t('intentPages.viberBotRegistration.faq.a3'),
    isOpen: false
  }
]);

onMounted(() => {
  document.querySelector("body")?.scrollIntoView();
});
</script>

<template>
  <div class="provider-intent-page">
    
    <!-- Hero Banner -->
    <div class="main-banner bg-gradient-to-br from-cyan-50 to-cyan-100 pt-[120px] pb-20 px-4">
      <div class="container mx-auto max-w-5xl text-center">
        <div class="flex justify-center mb-8">
           <a href="https://www.forbusiness.viber.com/ua/messaging-partners/smsbat/" target="_blank" rel="noopener noreferrer" class="transition-transform hover:-translate-y-1 block inline-block">
             <NuxtImg src="/images/partners/viber-silver-partner.png" width="130" height="188" format="webp" loading="lazy" alt="Rakuten Viber Official Silver Partner" class="h-24 md:h-32 w-auto drop-shadow-md object-contain" />
           </a>
        </div>
        <h1 class="text-4xl md:text-6xl font-extrabold text-[#3070b6] mb-6 leading-tight">
          {{ $t('intentPages.viberBotRegistration.heroTitle') }}
        </h1>
        <p class="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-10 leading-relaxed">
          {{ $t('intentPages.viberBotRegistration.heroSubtitle') }}
        </p>
        <div class="flex justify-center flex-wrap gap-4">
          <NuxtLink to="/register-bot" class="nav-link button filled px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition">
            {{ $t('botReg.regBot') }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- Features / Why Choose Us -->
    <div class="page-section py-20 bg-white">
      <div class="container mx-auto max-w-7xl px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-bold text-gray-900">{{ $t('intentPages.viberBotRegistration.whyTitle') }}</h2>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="feature-card p-8 rounded-2xl bg-cyan-50 border border-cyan-100 text-center hover:-translate-y-2 transition duration-300">
            <i class="pi pi-file-edit text-5xl text-[#3070b6] mb-6"></i>
            <h3 class="text-xl font-bold mb-4">{{ $t('intentPages.viberBotRegistration.whyItems[0].title') }}</h3>
            <p class="text-gray-600 leading-relaxed">
              {{ $t('intentPages.viberBotRegistration.whyItems[0].description') }}
            </p>
          </div>
          <div class="feature-card p-8 rounded-2xl bg-cyan-50 border border-cyan-100 text-center hover:-translate-y-2 transition duration-300">
            <i class="pi pi-shield text-5xl text-[#3070b6] mb-6"></i>
            <h3 class="text-xl font-bold mb-4">{{ $t('intentPages.viberBotRegistration.whyItems[1].title') }}</h3>
            <p class="text-gray-600 leading-relaxed">
              {{ $t('intentPages.viberBotRegistration.whyItems[1].description') }}
            </p>
          </div>
          <div class="feature-card p-8 rounded-2xl bg-cyan-50 border border-cyan-100 text-center hover:-translate-y-2 transition duration-300">
            <i class="pi pi-clock text-5xl text-[#3070b6] mb-6"></i>
            <h3 class="text-xl font-bold mb-4">{{ $t('intentPages.viberBotRegistration.whyItems[2].title') }}</h3>
            <p class="text-gray-600 leading-relaxed">
              {{ $t('intentPages.viberBotRegistration.whyItems[2].description') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- How To Start Funnel -->
    <HowToStart channel="viber-chatbots" country="" />

    <!-- Registration Form Wrapper -->
    <div class="page-section py-10 bg-white">
      <div class="container mx-auto max-w-4xl px-4">
        <div id="register-form" class="bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-gray-100">
          <div class="section-head text-center mb-10">
            <div class="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
              {{ $t('registerBotLanding.form.title') }}
            </div>
          </div>
          <RegisterBotForm />
        </div>
      </div>
    </div>

    <!-- FAQ Section for AI Context -->
    <div class="page-section py-20 bg-gray-50 border-t border-gray-100">
      <div class="container mx-auto max-w-4xl px-4">
        <PopularQuestions :title="$t('intentPages.viberBotRegistration.faqTitle')" :questions="faqItems" />
      </div>
    </div>
    
  </div>
</template>

<style scoped lang="scss">
.provider-intent-page {
  .button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
